import React, { useState } from 'react';
import { api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';
import TwoFactorSetupFlow from './TwoFactorSetupFlow';

export default function SecuritySettings({ user, onRefreshUser }) {
  const [step, setStep] = useState('idle'); // idle | setup | disable
  const [disablePassword, setDisablePassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingTimeout, setSavingTimeout] = useState(false);

  const TIMEOUT_OPTIONS = [
    { hours: 24, label: '24 hours' },
    { hours: 48, label: '48 hours' },
    { hours: 96, label: '96 hours (default)' },
    { hours: 168, label: '7 days' },
    { hours: 720, label: '30 days' },
  ];

  const changeSessionTimeout = async (hours) => {
    setSavingTimeout(true);
    try {
      await api.updateSessionTimeout(hours);
      onRefreshUser();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingTimeout(false);
    }
  };

  const finishSetup = () => {
    setStep('idle');
    onRefreshUser();
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.disable2FA(disablePassword);
      setDisablePassword('');
      setStep('idle');
      onRefreshUser();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CollapsiblePanel title="🔐 Security">
      {step === 'idle' && (
        <>
          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Two-Factor Authentication</span>
            <span style={{
              ...styles.statusBadge,
              color: user.totpEnabled ? '#00d4aa' : '#6b7280',
              borderColor: user.totpEnabled ? '#00d4aa' : '#374151',
            }}>
              {user.totpEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          {user.totpEnabled ? (
            <button style={styles.dangerBtn} onClick={() => setStep('disable')}>
              Disable 2FA
            </button>
          ) : (
            <>
              <p style={styles.helpText}>
                Add an extra layer of security using an authenticator app
                (Google Authenticator, Authy, Keeper, etc).
              </p>
              <button style={styles.primaryBtn} onClick={() => setStep('setup')}>
                Enable 2FA
              </button>
            </>
          )}

          <div style={styles.divider} />

          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Session Length</span>
          </div>
          <p style={styles.helpText}>
            How long you stay logged in before needing to sign in again.
            Applies to your whole household. Only affects future logins, not
            your current session.
          </p>
          <select
            style={styles.timeoutSelect}
            value={user.sessionTimeoutHours || 96}
            onChange={(e) => changeSessionTimeout(parseInt(e.target.value))}
            disabled={savingTimeout}
          >
            {TIMEOUT_OPTIONS.map((opt) => (
              <option key={opt.hours} value={opt.hours}>{opt.label}</option>
            ))}
          </select>
        </>
      )}

      {step === 'setup' && (
        <TwoFactorSetupFlow onComplete={finishSetup} onCancel={() => setStep('idle')} />
      )}

      {step === 'disable' && (
        <form onSubmit={submitDisable} style={styles.setupFlow}>
          <p style={styles.helpText}>Enter your password to disable 2FA.</p>
          <input
            style={styles.codeInput}
            type="password"
            placeholder="Current password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            autoFocus
            required
          />
          {error && <p style={styles.error}>{error}</p>}
          <div style={styles.btnRow}>
            <button type="button" style={styles.cancelBtn} onClick={() => setStep('idle')}>
              Cancel
            </button>
            <button type="submit" style={styles.dangerBtn} disabled={loading}>
              {loading ? '...' : 'Disable 2FA'}
            </button>
          </div>
        </form>
      )}
    </CollapsiblePanel>
  );
}

const styles = {
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  divider: {
    height: '1px',
    background: '#1e2a3a',
    margin: '1.25rem 0',
  },
  timeoutSelect: {
    width: '100%',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  statusLabel: {
    color: '#9ca3af',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  statusBadge: {
    border: '1px solid',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  primaryBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  dangerBtn: {
    background: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  cancelBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  setupFlow: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  codeInput: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.7rem',
    color: '#fff',
    fontSize: '1.1rem',
    textAlign: 'center',
    letterSpacing: '0.2em',
    fontFamily: "'DM Mono', monospace",
  },
  error: { color: '#ef4444', fontSize: '0.75rem', margin: 0 },
  btnRow: { display: 'flex', gap: '0.5rem' },
};
